import type { ChangeEvent } from 'react';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { TagVariants } from '@cencosud-cencommerce/paris-uikit';
import {
  Alert,
  CardBranch,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Skeleton,
  Text,
  toast
} from '@cencosud-cencommerce/paris-uikit';
import { yupResolver } from '@hookform/resolvers/yup';
import type { ObjectSchema } from 'yup';

import CommuneStoreAlert from '@/Application/Components/CommuneStoreAlert/CommuneStoreAlert';
import type { PickupItem } from '@/Application/Components/PickUpSelect/PickUpSelect';
import getShopsFromRegion from '@/Application/Modals/SelectShopModal/getShopsFromRegion';
import type { RegionFormValues } from '@/Domain/types/FormValues';
import { regionValidator } from '@/Domain/validators/regionValidator';
import type {
  Address,
  OptionSelected,
  TimeSlot
} from '@/modules/cart/domain/Cart';
import { dateFormatterString } from '@/modules/perseo-billing/utils';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import {
  type GtmGroupsData,
  ShippingEventInteractiveActions
} from '@/modules/shared/utils/gtmEvents/types';
import type { DeliveryProduct } from '@/modules/shippingMethod/domain/ShippingMethod';
import type { HttpClientPreferenceParams } from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import useCommunesOptions from '@/sections/region/hooks/useCommunesOption';
import RegionForm from '@/sections/region/RegionForm';
import useCollectShippingMethods from '@/sections/shippingMethods/hooks/useCollectShippingMethods';
import { getBrand } from '@/sections/shippingMethods/mappers/shippingMethodMapper';

interface Props {
  'data-testid'?: string;
  isOpen: boolean;
  onClose: VoidFunction;
  products?: DeliveryProduct[];
  shopType: 'copec' | 'paris';
  selectedPickup?: PickupItem;
  gtmData?: GtmGroupsData;
  cartShippingAddress?: Address;
  setOtherError: (value: boolean) => void;
  handleSelectClientOption: (
    param: HttpClientPreferenceParams,
    date?: string
  ) => void;
}

interface IRegionStores {
  options?: OptionSelected[];
  shippingMethod?: string;
  shippingType?: string;
  shippingSubType?: 'HOME' | 'IN_STORE' | 'COPEC';
  shipmentCostTotal?: string;
  cartShippingAddress?: Address;
}

const LABEL_NOT_AVAILABLE_CENCOSUD =
  'No hay tiendas Cencosud en tu comuna. Cámbiala o elige el retiro en puntos Blue Express y Copec';
const LABEL_NOT_AVAILABLE_COPEC =
  'No hay puntos Blue Express y Copec en tu comuna. Cámbiala o elige el retiro en tiendas Cencosud';

const FREE_TAG = {
  tag: { label: 'Gratis', variant: 'positive' as TagVariants }
};

const getProductAvailabilityLabel = (
  totalLineItems?: number,
  lineItemsAvailable?: number
) => {
  if (!totalLineItems || !lineItemsAvailable) return;

  if (totalLineItems === lineItemsAvailable) {
    return 'Todos tus productos están aquí';
  }

  if (lineItemsAvailable > 1) {
    return `${lineItemsAvailable} de tus productos están aquí`;
  }

  return `${lineItemsAvailable} de tu productos está aquí`;
};

const SelectShopModal: FC<Props> = ({
  'data-testid': dataTestId = 'paris-selectshopmodal',
  isOpen = true,
  onClose,
  products,
  shopType,
  selectedPickup,
  cartShippingAddress,
  gtmData,
  setOtherError,
  handleSelectClientOption
}) => {
  const { control, watch, resetField } = useForm<RegionFormValues>({
    defaultValues: {
      region: cartShippingAddress && +cartShippingAddress?.region,
      commune: cartShippingAddress?.commune
    },
    resolver: yupResolver(regionValidator as ObjectSchema<RegionFormValues>),
    mode: 'onChange'
  });
  const { getShopsByCommune, getCopecByCommune, loading } =
    useCollectShippingMethods();

  const isCopec = shopType === 'copec';

  const skus = useMemo(() => {
    return products?.map((product) => product.productSKU) ?? [];
  }, [products]);
  const communeOptions = useCommunesOptions(watch('region'));

  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedCommune, setSelectedCommune] = useState<string>('');
  const [regionStores, setRegionStores] = useState<IRegionStores | null>(null);
  const [isCommuneStores, setIsCommuneStores] = useState<boolean>(
    selectedPickup?.hasCommuneOption || true
  );
  const [selectedStore, setSelectedStore] = useState<OptionSelected | null>(
    null
  );
  const [itemsInDeliveryGroup, setItemsInDeliveryGroup] = useState<number>();

  const handleClose = () => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-tag': `${selectedRegion}/${selectedCommune}`,
        'event-interactive-accion':
          ShippingEventInteractiveActions.CloseButtonPickupModal,
        'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`
      }
    });
    onClose();
  };

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (isInitialLoad && isOpen) {
      const fetchDeliveryGroup = async () => {
        let deliveryGroup;

        if (cartShippingAddress) {
          const commune = cartShippingAddress.commune;
          if (isCopec) {
            try {
              deliveryGroup = await getCopecByCommune(commune, skus);
              setItemsInDeliveryGroup(deliveryGroup.lineItemsInDeliveryGroup);
            } catch (error) {
              setOtherError(true);
            }
          } else {
            try {
              deliveryGroup = await getShopsByCommune(commune, skus);
              setItemsInDeliveryGroup(deliveryGroup.lineItemsInDeliveryGroup);
            } catch (error) {
              setOtherError(true);
            }
          }
          if (deliveryGroup) {
            const shippingOptions = deliveryGroup?.shippingOptions?.[0];

            const isCommuneStore = shippingOptions?.options?.find(
              (option) =>
                option.collectNodeAddress?.collectComunaCode === commune
            );
            setIsCommuneStores(Boolean(isCommuneStore));

            if (shippingOptions) {
              setRegionStores(
                getShopsFromRegion(shippingOptions, commune, !!isCommuneStore)
              );
            } else {
              setRegionStores({
                options: []
              });
            }

            setIsInitialLoad(false);
          } else {
            setRegionStores({
              options: []
            });
          }
        }
      };

      fetchDeliveryGroup();
    }
  }, [
    isInitialLoad,
    cartShippingAddress,
    skus,
    getCopecByCommune,
    getShopsByCommune,
    isOpen,
    setOtherError,
    isCopec
  ]);

  const handleOnChangeComuna = async (event: ChangeEvent<HTMLInputElement>) => {
    const communeValue = event.target.value;
    setSelectedStore(null);
    setRegionStores(null);
    setSelectedCommune(
      communeOptions.find((c) => c.value === communeValue)?.label || ''
    );
    let deliveryGroup;

    if (isCopec) {
      try {
        deliveryGroup = await getCopecByCommune(communeValue, skus);
      } catch (error) {
        setOtherError(true);
      }
    } else {
      try {
        deliveryGroup = await getShopsByCommune(communeValue, skus);
      } catch (error) {
        setOtherError(true);
      }
    }

    if (deliveryGroup) {
      const shippingOptions = deliveryGroup?.shippingOptions?.[0];

      const isCommuneStore = shippingOptions?.options?.find(
        (option) =>
          option.collectNodeAddress?.collectComunaCode === communeValue
      );
      setIsCommuneStores(Boolean(isCommuneStore));

      if (shippingOptions) {
        setRegionStores(
          getShopsFromRegion(shippingOptions, communeValue, !!isCommuneStore)
        );
      } else {
        setRegionStores({
          options: []
        });
      }
    } else {
      setRegionStores({
        options: []
      });
    }
  };

  const sendResultEvent = (
    title: string,
    retiro: string,
    dispatchGroup: string
  ) => {
    if (shopType !== 'copec') {
      // Solo para retiro en tienda
      if (!isCommuneStores) {
        SendShippingEvents.sendEvent({
          event: 'baseEvent',
          extraFields: {
            'event-interactive-tag': title,
            'event-interactive-accion':
              ShippingEventInteractiveActions.PickupNotAllowed,
            'event-interactive-retiro': retiro,
            'event-interactive-grupo_despacho': dispatchGroup
          }
        });
      }
      SendShippingEvents.sendEvent({
        event: 'baseEvent',
        extraFields: {
          'event-interactive-tag': title,
          'event-interactive-accion': ShippingEventInteractiveActions.Pickup,
          'event-interactive-retiro': retiro,
          'event-interactive-grupo_despacho': dispatchGroup
        }
      });
    }
  };

  const sendShippingEvents = ({
    action,
    isCopec
  }: {
    action: ShippingEventInteractiveActions;
    isCopec: boolean;
  }) => {
    const timeSlots =
      (selectedStore as { timeSlots?: TimeSlot[] })?.timeSlots ?? [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseFields: Record<string, any> = {
      'event-interactive-accion': action,
      'event-interactive-tag': selectedStore?.collectNodeName,
      'event-interactive-vendedor': selectedStore?.collectNodeAdmin,
      'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`,
      'event-interactive-retiro': timeSlots.length
        ? timeSlots
            .map((t) => `${t.deliveryTimeStart}-${t.deliveryTimeEnd}`)
            .join(' / ')
        : ''
    };
    if (isCopec) {
      baseFields['event-interactive-costo'] = regionStores?.shipmentCostTotal;
    }

    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: baseFields
    });
  };

  useEffect(() => {
    if (selectedPickup && !selectedStore && regionStores?.options?.length) {
      const defaultStore = regionStores.options.find(
        (store) => store.collectNodeCode === selectedPickup.collectNodeCode
      );

      if (defaultStore) {
        setSelectedStore(defaultStore);
      }
    }
  }, [selectedPickup, regionStores, selectedStore]);

  useEffect(() => {
    if (!isCommuneStores && isOpen && regionStores && selectedStore) {
      const date = new Date(selectedStore.shipDate);
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, '0')}/${date.getFullYear()}`;
      SendShippingEvents.sendEvent({
        event: 'baseEvent',
        extraFields: {
          'event-interactive-accion':
            ShippingEventInteractiveActions.PickupNotAvailable,
          'event-interactive-tag': selectedStore?.collectNodeName,
          'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`,
          'event-interactive-retiro': formattedDate,
          'event-interactive-vendedor': selectedStore?.collectNodeAdmin
        }
      });
    }
  }, [isCommuneStores, isOpen, regionStores, selectedStore, gtmData]);

  const headerTag = isCopec ? {} : FREE_TAG;

  const noOptionsAvailableMessage = isCopec
    ? LABEL_NOT_AVAILABLE_COPEC
    : LABEL_NOT_AVAILABLE_CENCOSUD;

  return (
    <Modal
      data-testid={dataTestId}
      isOpen={isOpen}
      className="tablet_w:co-min-h-[90vh] desktop:co-min-h-[auto] desktop:co-max-h-[90vh]"
    >
      <ModalHeader
        {...headerTag}
        className="[&>div]:co-grid [&>div]:co-grid-cols-[48px,auto,50px] [&>div]:co-w-full [&>div]:co-mr-2"
        onClose={handleClose}
        title={`Retiro en ${shopType === 'copec' ? 'Puntos Blue Express y Copec' : 'tienda'}`}
        icon={{
          name: 'il-store',
          fill: 'primary-500'
        }}
      />
      <ModalBody className="tablet:co-min-h-[400px] co-m-0 co-pt-2 co-pl-1 co-pr-5 co-pb-6 co-gap-6">
        <div className="co-flex co-flex-col co-gap-3 co-pr-2 co-pt-2 ">
          {!isCommuneStores && <CommuneStoreAlert />}
          <Text variant="body1" weight={600}>
            ¿Dónde quieres retirar?
          </Text>
          <RegionForm
            control={control}
            watch={watch}
            resetField={resetField}
            onChangeComuna={handleOnChangeComuna}
            onSelectRegion={(region: string) => setSelectedRegion(region)}
          />

          {!regionStores ? (
            <div className="co-flex co-flex-col co-gap-2 co-w-full co-mt-6">
              <Skeleton isText animation isVisible width={142} height={24} />
              <Skeleton
                animation
                isVisible
                height={144}
                className="co-w-full"
              />
            </div>
          ) : null}

          {regionStores?.options && (
            <div className="co-flex co-flex-col co-gap-2 co-mt-6">
              <Text variant="body2" weight={600} className="co-mb-2">
                {isCopec ? 'Puntos Blue Express y Copec' : 'Tiendas Cencosud'}
              </Text>
              {regionStores.options?.length === 0 && (
                <Alert status="info" title={noOptionsAvailableMessage} />
              )}
              {regionStores.options?.map((store, index) => {
                store.shippingSubType = regionStores.shippingSubType;
                const date = new Date(store.deliveryDateFrom);
                const formattedDate = date.toLocaleDateString('es-CL', {
                  day: 'numeric',
                  month: 'long'
                });
                const address = `${store.collectNodeAddress?.collectNodeAddress1} ${
                  store.collectNodeAddress?.collectNodeAddress2
                }, ${store.collectNodeAddress?.collectComunaName}`;

                const brand = getBrand(store?.collectNodeAdmin ?? '');

                return (
                  <CardBranch
                    title={store?.collectNodeName ?? ''}
                    brand={brand}
                    key={`${store.collectNodeCode}-${store.originNodeCode}-${index}`}
                    description1Label="Retira desde:"
                    description1Value={formattedDate}
                    infoTagLabel={getProductAvailabilityLabel(
                      itemsInDeliveryGroup,
                      store.lineItemsInNode
                    )}
                    description2={address}
                    radioButton={{
                      onChange: () => setSelectedStore(store),
                      defaultChecked:
                        selectedPickup?.collectNodeCode ===
                        store.collectNodeCode,
                      id: `${store.collectNodeCode}-${index}`,
                      value: store.collectNodeCode,
                      name: 'storeSelector'
                    }}
                    showBorder={true}
                  />
                );
              })}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter
        primaryButton={{
          label: 'Confirmar',
          disabled: !selectedStore,
          loading: loading,
          onClick: async () => {
            if (!selectedStore) return;

            if (!isCommuneStores) {
              if (shopType === 'copec') {
                sendShippingEvents({
                  action:
                    ShippingEventInteractiveActions.CopecPickupNotAvailable,
                  isCopec: true
                });
              } else {
                sendShippingEvents({
                  action:
                    ShippingEventInteractiveActions.StorePickupNotAvailable,
                  isCopec: false
                });
              }
            }
            if (shopType === 'copec') {
              sendShippingEvents({
                action: ShippingEventInteractiveActions.ClickConfirmChangeCopec,
                isCopec: true
              });
            } else {
              sendShippingEvents({
                action: ShippingEventInteractiveActions.nuevo,
                isCopec: false
              });
            }

            const title = 'Cambiaste la tienda con éxito';
            const retiro = `${selectedStore.collectNodeName} / ${dateFormatterString(selectedStore.shipDate)}`;
            const dispatchGroup = `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`;

            try {
              handleSelectClientOption({
                shippingMethod: regionStores?.shippingMethod,
                shippingType: regionStores?.shippingType,
                shippingSubType: selectedStore.shippingSubType,
                nodeCode: selectedStore.collectNodeCode,
                collectRegionCode:
                  selectedStore.collectNodeAddress?.collectRegionCode
              });

              sendResultEvent(title, retiro, dispatchGroup);
              toast({ title });
              onClose();
            } catch (error) {
              const title = 'No pudimos cambiar la tienda';
              sendResultEvent(title, retiro, dispatchGroup);
              toast({
                status: 'error',
                title
              });
              onClose();
              return undefined;
            }
          }
        }}
      />
    </Modal>
  );
};

export default SelectShopModal;
